import React, { useState, useEffect } from 'react';
import FormTitle from '../../../../components/FormTitle';
import CustomButton from '../../../../components/AddButton';
import { Plus, Upload } from '../../../../common/svg';
import SecureImage from "../../../../components/BucketImage/Image";
import FileViewer from '../../../../components/FileViwe/FileViewer';

// Define types for the component
interface UploadedImage {
  url: string;
  name: string;
  id?: string;
  originalName?: string;
}

interface UploadedFile {
  fileId?: string;
  fileName: string;
  originalFileName?: string;
  fileSize?: number;
  fileType?: string;
  s3BucketName?: string;
  s3ObjectKey?: string;
  eTag?: string;
  url?: string;
  file?: File;
  id?: string;
}

interface DocumentComponentProps {
  uploadedImages: UploadedImage[];
  setUploadedImages: React.Dispatch<React.SetStateAction<UploadedImage[]>>;
  uploadedFiles?: UploadedFile[];
  setUploadedFiles?: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
  files: File[];
  setFiles: React.Dispatch<React.SetStateAction<File[]>>;
  isEditMode?: boolean;
}

const DocumentComponent: React.FC<DocumentComponentProps> = ({
  uploadedImages,
  setUploadedImages,
  uploadedFiles = [],
  setUploadedFiles,
  files,
  setFiles,
  isEditMode = false
}) => {
  const [duplicateFiles, setDuplicateFiles] = useState<File[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [removedFileIds, setRemovedFileIds] = useState<string[]>([]);
  
  // Helper function to generate a unique ID
  const generateUniqueId = (): string => {
    return Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
  };
  
  // Determine file types that should use the FileViewer
  const isViewableFile = (fileType: string): boolean => {
    const viewableTypes = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt', 'csv', 'jpg', 'jpeg', 'png'];
    return viewableTypes.includes(fileType.toLowerCase());
  };

  // File type icon mapping
  const getFileTypeIcon = (fileType: string): string => {
    const fileIconMap: Record<string, string> = {
      pdf: Upload,
      doc: Upload,
      docx: Upload,
      xls: Upload,
      xlsx: Upload,
      default: Upload
    };
    
    return fileIconMap[fileType.toLowerCase()] || fileIconMap.default;
  };

  const handleUploadClick = () => {
    const fileInput = document.getElementById('fileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFiles = Array.from(e.target.files);
      
      // Check for duplicate files using originalFileName to identify duplicates correctly
      const existingFileNames = [
        ...files.map(file => file.name),
        ...(uploadedFiles?.map(file => file.originalFileName || file.fileName) || [])
      ];

      const duplicates = selectedFiles.filter(file => 
        existingFileNames.includes(file.name) && 
        !removedFileIds.includes(file.name)
      );
      
      const uniqueFiles = selectedFiles.filter(file => 
        !existingFileNames.includes(file.name) || 
        removedFileIds.includes(file.name)
      );

      if (duplicates.length > 0) {
        setDuplicateFiles(duplicates);
        setPendingFiles(uniqueFiles);
      } else {
        processFiles(uniqueFiles);
      }

      // Clear the input value to allow selecting the same file again
      e.target.value = '';
    }
  };

  // Get file extension from a file name
  const getFileExtension = (fileName: string): string => {
    return fileName.split('.').pop()?.toLowerCase() || '';
  };

  // Check if a file is an image
  const isImageFile = (file: File): boolean => {
    return file.type.startsWith('image/');
  };
  
  // Check if a file is a PDF
  const isPdfFile = (file: File): boolean => {
    return file.type === 'application/pdf' || getFileExtension(file.name) === 'pdf';
  };

  const processFiles = (fileList: File[]) => {
    // Add all files to the main files array
    const newFiles = fileList.map(file => {
      // Remove this file from removedFileIds if it was previously removed
      setRemovedFileIds(prev => prev.filter(id => id !== file.name));
      return file;
    });
    
    setFiles(prev => [...prev, ...newFiles]);
    
    // Process image files and other files separately
    const imageFiles: File[] = [];
    const otherFiles: File[] = [];
    
    fileList.forEach(file => {
      if (isImageFile(file)) {
        imageFiles.push(file);
      } else {
        otherFiles.push(file);
      }
    });
    
    // Add to uploadedImages array
    if (imageFiles.length > 0) {
      const newUploadedImages = imageFiles.map(file => ({
        url: URL.createObjectURL(file),
        name: file.name,
        originalName: file.name, // Store original name for tracking
        id: generateUniqueId()
      }));
      setUploadedImages(prev => [...prev, ...newUploadedImages]);
    }
    
    // Add to uploadedFiles array if setUploadedFiles is provided
    if (setUploadedFiles) {
      // Only add non-image files to uploadedFiles to prevent duplication
      const newUploadedFiles = otherFiles.map(file => ({
        fileName: file.name,
        originalFileName: file.name, // Store original name for tracking
        fileSize: file.size,
        fileType: getFileExtension(file.name),
        url: URL.createObjectURL(file),
        file: file,
        id: generateUniqueId()
      }));
      
      setUploadedFiles(prev => [...prev, ...newUploadedFiles]);
    }
  };

  useEffect(() => {
    if (duplicateFiles.length > 0) {
      const message =
        `Duplicate files detected:\n${duplicateFiles.map(f => f.name).join('\n')}\n\n` +
        `Do you want to upload these files anyway?`;

      const userConfirmed = window.confirm(message);

      if (userConfirmed) {
        processFiles([...pendingFiles, ...duplicateFiles]);
      } else {
        processFiles(pendingFiles);
      }

      setDuplicateFiles([]);
      setPendingFiles([]);
    }
  }, [duplicateFiles]);

  const handleRemoveImage = (index: number) => {
    // Find the image URL to revoke the object URL
    const imageUrl = uploadedImages[index]?.url;
    if (imageUrl && imageUrl.startsWith('blob:')) {
      URL.revokeObjectURL(imageUrl);
    }
    
    // Get the ORIGINAL file name to remove from files array
    const originalFileName = uploadedImages[index]?.originalName || uploadedImages[index]?.name;
    
    // Add to removed file IDs using the original name
    if (originalFileName) {
      setRemovedFileIds(prev => [...prev, originalFileName]);
    }
    
    // Remove from uploadedImages array
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
    
    // Also remove from files array by matching the original file name
    if (originalFileName) {
      setFiles(prev => prev.filter(file => file.name !== originalFileName));
    }
  };

  const handleRemoveFile = (fileIdOrIndex: string | number) => {
    if (setUploadedFiles) {
      setUploadedFiles(prev => {
        const fileIndex = typeof fileIdOrIndex === 'string' 
          ? prev.findIndex(file => file.fileId === fileIdOrIndex || file.id === fileIdOrIndex)
          : fileIdOrIndex as number;
        
        if (fileIndex !== -1) {
          // Get ORIGINAL file name for removal from files array
          const originalFileName = prev[fileIndex]?.originalFileName || prev[fileIndex]?.fileName;
          
          // Revoke object URL if it exists
          const fileUrl = prev[fileIndex]?.url;
          if (fileUrl && fileUrl.startsWith('blob:')) {
            URL.revokeObjectURL(fileUrl);
          }
          
          // Add to removed file IDs using the original name
          if (originalFileName) {
            setRemovedFileIds(prev => [...prev, originalFileName]);
          }
          
          // Remove from files array by matching the original file name
          if (originalFileName) {
            setFiles(prevFiles => prevFiles.filter(file => file.name !== originalFileName));
          }
          
          // Remove the file from uploadedFiles
          return prev.filter((_, i) => i !== fileIndex);
        }
        return prev;
      });
    }
  };

  // Add this handler to prevent form submission on Enter key
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // On Enter, blur the input to "accept" the change
      (e.target as HTMLInputElement).blur();
    }
  };

  // Handler for updating file names
  const handleFileNameChange = (index: number, newName: string) => {
    if (setUploadedFiles) {
      setUploadedFiles(prev => {
        const newFiles = [...prev];
        if (newFiles[index]) {
          // Store original name if not already stored
          if (!newFiles[index].originalFileName) {
            newFiles[index].originalFileName = newFiles[index].fileName;
          }
          // Update display file name
          newFiles[index].fileName = newName;
        }
        return newFiles;
      });
    }
  };

  // Handler for updating image names
  const handleImageNameChange = (index: number, newName: string) => {
    setUploadedImages(prev => {
      const newImages = [...prev];
      if (newImages[index]) {
        // Store original name if not already stored
        if (!newImages[index].originalName) {
          newImages[index].originalName = newImages[index].name;
        }
        // Update display image name
        newImages[index].name = newName;
      }
      return newImages;
    });
  };

  // Function to handle the onBlur event when input loses focus
  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Additional validation could be added here if needed
    console.log("Input field blur event - name accepted");
  };

  // Function to render appropriate file preview based on type
  const renderFilePreview = (file: UploadedFile, index: number) => {
    const fileType = file.fileType?.toLowerCase() || getFileExtension(file.fileName).toLowerCase();
    
    if (file.url) {
      // For locally uploaded files with preview URL
      if (fileType === 'pdf') {
        return (
          <object
            data={file.url}
            type="application/pdf"
            className="w-full h-[120px] sm:h-[140px] object-cover rounded-md"
          >
            <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-md">
              <span className="text-sm text-gray-700">PDF Preview</span>
            </div>
          </object>
        );
      } else if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(fileType)) {
        return (
          <img
            src={file.url}
            alt={file.fileName}
            className="w-full h-[120px] sm:h-[140px] object-cover rounded-md"
          />
        );
      }
    }
    
    // For files from S3
    if (file.s3BucketName && file.s3ObjectKey) {
      return (
        <div className="w-full h-[120px] sm:h-[140px] overflow-hidden rounded-md">
          <FileViewer
            bucketName={file.s3BucketName} 
            fileKey={file.s3ObjectKey} 
          />
        </div>
      );
    }
    
    // Default file type icon
    return (
      <div className="w-full h-[120px] sm:h-[140px] flex flex-col items-center justify-center bg-gray-100 rounded-md">
        <SecureImage 
          fileKey={getFileTypeIcon(fileType || 'default')} 
          alt="File icon" 
          width="60px" 
        />
        <span className="text-sm text-gray-700 mt-2 truncate w-[90%] text-center">
          {file.fileName}
        </span>
      </div>
    );
  };

  return (
    <div className="bg-white p-4 sm:p-6 rounded-[20px]">
      <div className="flex items-center justify-between mb-4">
        <FormTitle title="Documents" />
        <CustomButton label="" icon={Plus} onClick={handleUploadClick} />
      </div>

      {/* Wrap content in a non-submitting form to prevent default form submission */}
      <div className="w-full" onSubmit={(e) => e.preventDefault()}>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {/* Display uploaded files */}
          {uploadedFiles.map((file, index) => (
            <div
              key={`file-${file.fileId || file.id || index}`}
              className="relative p-2 sm:p-4 w-full h-[180px] sm:h-[200px] rounded-[20px] flex justify-center items-center flex-col border border-gray-200"
            >
              {/* Use the renderFilePreview function to show the appropriate preview */}
              {renderFilePreview(file, index)}
              
              {/* Make file name editable */}
              <input
                type="text"
                value={file.fileName}
                onChange={(e) => handleFileNameChange(index, e.target.value)}
                onKeyDown={handleInputKeyDown}
                onBlur={handleInputBlur}
                className="mt-2 text-center text-xs sm:text-sm w-full border border-gray-300 rounded-md p-1"
              />
              
              {/* Only show remove button if we have a way to update the files */}
              {setUploadedFiles && (
                <button
                  onClick={() => handleRemoveFile(file.fileId || file.id || index)}
                  className="absolute top-1 right-1 sm:top-2 sm:right-2 bg-gray-500 text-white text-xs rounded-[30px] h-[20px] w-[20px] flex items-center justify-center"
                  aria-label="Remove file"
                  type="button"
                >
                  &times;
                </button>
              )}
            </div>
          ))}

          {/* Display uploaded images (if needed separately) */}
          {uploadedImages.map((image, index) => (
            <div
              key={`image-${image.id || index}`}
              className="relative p-2 sm:p-4 w-full h-[180px] sm:h-[200px] rounded-[20px] flex justify-center items-center flex-col border border-gray-200"
            >
              <img
                src={image.url}
                alt={image.name}
                className="w-full h-[120px] sm:h-[140px] object-cover rounded-md"
              />

              <input
                type="text"
                value={image.name}
                onChange={(e) => handleImageNameChange(index, e.target.value)}
                onKeyDown={handleInputKeyDown}
                onBlur={handleInputBlur}
                className="mt-2 text-center text-xs sm:text-sm w-full border border-gray-300 rounded-md p-1"
              />

              <button
                onClick={() => handleRemoveImage(index)}
                className="absolute top-1 right-1 sm:top-2 sm:right-2 bg-gray-500 text-white text-xs rounded-[30px] h-[20px] w-[20px] flex items-center justify-center"
                aria-label="Remove file"
                type="button"
              >
                &times;
              </button>
            </div>
          ))}

          {/* Upload button tile */}
          <div
            className="flex flex-col items-center justify-center cursor-pointer p-2 sm:p-4 w-full h-[180px] sm:h-[200px] rounded-[20px] border border-dashed border-gray-300 hover:border-gray-400 transition-colors"
            onClick={handleUploadClick}
          >
            <SecureImage fileKey={Upload} alt="Upload icon" width="60px" />
            <p className="text-dark-800 font-poppins text-[12px] sm:text-[14px] text-center whitespace-nowrap mt-2">
              Upload Documents
            </p>
          </div>
          
          <input
            type="file"
            id="fileInput"
            className="hidden"
            onChange={handleFileUpload}
            multiple
            accept="*/*" // Accept all file types
          />
        </div>
      </div>
    </div>
  );
};

export default DocumentComponent;